// Turns a stored blog body into the HTML BlogContent renders.
//
// Posts are written as Markdown by the admin editor; older posts are still the
// editor's HTML. Both come out as the SAME markup the page has always styled —
// <h2>, <p>, <ul><li><p>, <blockquote><p>, <pre><code>, <img>, <a target=_blank>
// — so the post typography (the class list on BlogContent) applies unchanged.
//
// Markdown is converted with the pipeline react-markdown uses (remark-parse +
// GFM + remark-rehype), so it reads exactly like the admin preview. Raw HTML in
// Markdown is dropped (remark-rehype's default); the result is still passed
// through BlogContent's DOMPurify allowlist like any other body.

import { defaultUrlTransform } from "react-markdown";
import { unified, type Plugin } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";

/** The minimum of an mdast/hast node these plugins touch. */
interface TreeNode {
  type: string;
  tagName?: string;
  spread?: boolean;
  properties?: Record<string, unknown>;
  children?: TreeNode[];
}

function walk(node: TreeNode, visit: (node: TreeNode) => void) {
  visit(node);
  node.children?.forEach((child) => walk(child, visit));
}

// The editor's HTML always wraps a list item's text in <p>, and the page's
// list spacing is tuned for that. Markdown writes lists "tight" (no <p>), so
// mark them loose to get the same markup.
const looseLists: Plugin = () => (tree) =>
  walk(tree as TreeNode, (node) => {
    if (node.type === "list" || node.type === "listItem") node.spread = true;
  });

// An image's size and alignment ride in its URL fragment, written by the admin
// editor: `![alt](/img.png#w=60&align=left)` (see encrata-admin's
// src/lib/blog/image-layout.ts — keep the two in step). They become classes from
// globals.css (`blog-img-w-{20..95}`, `blog-img-{left,center,right}`): the
// sanitizer allows `class` but not `style`. Widths snap to steps of 5.
function imageLayout(href: string): { src: string; classes: string[] } {
  const hash = href.indexOf("#");
  if (hash === -1) return { src: href, classes: [] };
  const params = new URLSearchParams(href.slice(hash + 1));
  if (!params.has("w") && !params.has("align")) return { src: href, classes: [] };

  const classes: string[] = [];
  const w = Number(params.get("w"));
  if (Number.isFinite(w) && w > 0) {
    const snapped = Math.min(100, Math.max(20, Math.round(w / 5) * 5));
    if (snapped < 100) classes.push(`blog-img-w-${snapped}`);
  }
  const align = params.get("align");
  if (align === "left" || align === "right") classes.push(`blog-img-${align}`);
  else if (classes.length) classes.push("blog-img-center");
  return { src: href.slice(0, hash), classes };
}

// Links and images get the URL check react-markdown applies (and so the admin
// preview): javascript:, data: and other unsafe schemes become "". Links then
// open in a new tab, as the editor's HTML always did.
const safeLinks: Plugin = () => (tree) =>
  walk(tree as TreeNode, (node) => {
    if (node.type !== "element") return;
    const props = node.properties ?? {};
    if (node.tagName === "a") {
      node.properties = {
        ...props,
        href: defaultUrlTransform(String(props.href ?? "")),
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      };
    } else if (node.tagName === "img") {
      const { src, classes } = imageLayout(String(props.src ?? ""));
      node.properties = { ...props, src: defaultUrlTransform(src) };
      if (classes.length) node.properties.className = classes;
    }
  });

const markdown = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(looseLists)
  .use(remarkRehype)
  .use(safeLinks)
  .use(rehypeStringify)
  .freeze();

/**
 * Whether a stored body is legacy HTML rather than Markdown. The editor's HTML
 * always opens with a block tag; Markdown never does in practice. Mirrors the
 * admin editor's check, which decides how it loads the same field.
 */
export function isLegacyHTML(content: string): boolean {
  return /^\s*<(p|h[1-6]|ul|ol|blockquote|pre|img|video|figure|hr|div|table)[\s/>]/i.test(content);
}

/** The body as HTML: legacy HTML as stored, Markdown converted. Not yet sanitized. */
export function blogBodyToHTML(content: string): string {
  if (isLegacyHTML(content)) return content;
  return String(markdown.processSync(content));
}
